import * as R from 'ramda';
import { compare, validate } from 'compare-versions';

// Mutable exports in order to allow them to be overridden in development
/* eslint-disable import/no-mutable-exports */

const environmentSpec = {
  'pos-dev': 'develop',
  'pos-sb': 'sandbox',
  epos: 'production',
  'epos-au': 'production',
  eposp: 'pnp',
  'eposp-test': 'pnp-test',
  localhost: 'localhost',
};

let getSubdomain = () => window.location.hostname.split('.')?.[0];
/**
 * Returns the first parameter in the pathname - POS version
 */
export const getVersionParamFromURL = () =>
  window.location.pathname.split('/')?.[1];

/**
 * Checks if the URL is production or sandbox
 */
export const checkIsAllowedEnvironment = () =>
  Object.keys(environmentSpec).includes(getSubdomain());

/**
 * Returns environment based on the URL
 */
export const getEnvironmentFromURL = ():
  | typeof environmentSpec[keyof typeof environmentSpec]
  | '' => {
  const env = getSubdomain();
  return environmentSpec[env] ?? '';
};

/**
 * Returns the version query parameter from the URL
 */
export const getVersionQueryParamFromURL = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('version');
};

export const LAST_VC_REDIRECT_VERSION = 'last_version_control_redirect';

/**
 * Checks if the current version is already
 * @param version - version to redirect to
 */
export const checkIsSecondRedirectAttempt = version => {
  const lastVersionFromRedirect = sessionStorage.getItem(
    LAST_VC_REDIRECT_VERSION,
  );
  return lastVersionFromRedirect === version;
};

/**
 * Redirects POS to the version passed from parameters
 */
export const redirectPOSToVersion = (version: string) => {
  const posLocation = new URL(window.location.origin.toString());
  posLocation.searchParams.set('version', version);
  window.location.replace(posLocation.toString());
};

const ALL_VERSIONS_URL = 'https://app-ver.erply.com';

const versionsFromTxtToArray = R.pipe(
  R.replace(/(\s|\n)*/g, ''),
  R.split(','),
  R.filter(a => R.not(R.isEmpty(a))),
);

/**
 * Fetches all available versions for the current environment,
 * if the current environment is production or sandbox
 */
export let getPOSAvailableVersions = () => {
  const environment = getSubdomain();
  if (checkIsAllowedEnvironment()) {
    return window
      .fetch(`${ALL_VERSIONS_URL}/${environment}/all.txt`)
      .then(r => r.text())
      .then(versionsFromTxtToArray)
      .catch(err => {
        console.error('Failed to load available POS versions', err);
        return [];
      }) as Promise<string[]>;
  }
  return Promise.resolve([]);
};

/**
 * Filters out versions before the passed version.
 * @param semVer - semantic version
 * @param versions - array of semantic versions
 */
export const filterOutVersionBeforeSemVer = (
  semVer: string,
  versions: string[],
) => {
  if (!validate(semVer)) {
    return versions;
  }
  return versions.filter(v => {
    if (!validate(v)) {
      return true;
    }
    return compare(v, semVer, '>=');
  });
};

export let verifyTargetAvailable = target => {
  const posLocation = new URL(window.location.origin.toString());
  posLocation.pathname = target;
  return window.fetch(posLocation.toString(), { method: 'HEAD' }).then(res => {
    if (res.redirected) {
      if (!res.url.includes(target)) {
        // On redirect off, throw error
        throw new Error(`Target version ${target} redirects to ${res.url}`);
      } else {
        // On redirect that includes version (maybe just toggling a trailing slash or something), warn but proceed
        console.warn(
          `Target version ${target} redirected to ${res.url}, but that url still contains desired version`,
        );
      }
    }
  });
};

if (process.env.NODE_ENV === 'development') {
  const POS_ALL_VERSIONS = [
    '4.40.0', // Not shown because older than 4.43.0
    '4.52.0',
    '4.52.1',
    '4.52.2',
    '4.53.0-rc.1',
    '4.53.0-rc.2',
    '4.53.0-rc.3',
    '4.53.0-deleted',
    '4.50.0',
    '4.53.0',
    '4.53.1',
    'PBIB-1234', // not semver so always shown
  ];
  const VERSIONS_UNAVAILABLE = ['4.53.0-deleted'];
  const VERSION_CHECK_LOADING_TIME = 0.5e3;
  const POS_ALL_VERSIONS_LOADING_TIME = 3e3;
  // Override apparent subdomain in URL (used to determine environment)
  getSubdomain = () => 'localhost';
  // Override all.txt versions list (used for version select)
  getPOSAvailableVersions = () =>
    new Promise(res => {
      setTimeout(() => res(POS_ALL_VERSIONS), POS_ALL_VERSIONS_LOADING_TIME);
    });
  // Override version check
  verifyTargetAvailable = (targetVersion: string) =>
    new Promise((res, rej) => {
      setTimeout(() => {
        if (VERSIONS_UNAVAILABLE.includes(targetVersion)) {
          rej(
            new Error(
              `[DEVELOPMENT ONLY] Target version ${targetVersion} is listed in VERSIONS_UNAVAILABLE`,
            ),
          );
        } else {
          res();
        }
      }, VERSION_CHECK_LOADING_TIME);
    });
  // Simulate serverside behaviour
  (function simulateServersideBehaviour() {
    // redirect for URLS with version query param
    // localhost.erply.com/latest?version=1.2.3        →        localhost.erply.com/1.2.3/
    const location = new URL(window.location.href);
    const versionParam = location.searchParams.get('version');
    if (versionParam) {
      location.searchParams.delete('version');
      location.pathname = `/${versionParam}/`;
      return window.location.replace(location.href);
    }
    // Redirect for URLs with no path (default to latest)
    // localhost.erply.com/        →        localhost.erply.com/latest/
    if (getVersionParamFromURL() === '') {
      // Simulate serv
      location.pathname = 'latest/';
      return window.location.replace(location.href);
    }
    return undefined;
  })();
}
